import { useQuery, useMutation } from 'react-query';
import dayjs from 'dayjs';

import { request, useCurrentUserId, toastError, formatRanking } from '@/utils';

import { ProposalItem, ProposalVoter } from '../types';

export const useCreateProposal = () => {
  return useMutation((data: { description: string; addr_list?: string[]; url_list?: string[] }) => {
    return request.post<{ proposal: ProposalItem }>('/proposal/create', data);
  });
};
export const useCheckProposalDuplicate = () => {
  return useMutation((data: { description: string; addr_list?: string[]; url_list?: string[] }) => {
    return request.post<{ is_exists: boolean; proposal_id: string }>(
      '/proposal/check_exists',
      data,
    );
  });
};
export const useCheckProposalSimilar = () => {
  return useMutation((data: { addr_list?: string[]; url_list?: string[] }) => {
    return request.post<{ proposals: ProposalItem[] }>('/proposal/similar', data).then(res => {
      const proposals = res.proposals.map(item => transProposalDetail(item));
      return proposals;
    });
  });
};

const ONE_DAY_SECONDS = 60 * 60 * 24;

export const transProposalDetail = (data: ProposalItem) => {
  const currentTime = +new Date() / 1000;

  const createAt = dayjs.unix(data.create_at);
  const duration = currentTime - data.create_at;
  data._date =
    duration >= ONE_DAY_SECONDS
      ? createAt.format('YYYY-MM-DD')
      : `${dayjs.duration(duration, 'seconds').humanize()}`;

  data._usdValue = `${formatRanking(data.usd_value)}`;

  return data;
};

export const useProposalDetail = (id: string) => {
  const userId = useCurrentUserId();
  const url = '/proposal';

  return useQuery(
    [url, userId, id],
    () =>
      request
        .get<{
          proposal: ProposalItem;
        }>(url, {
          id,
        })
        .then(x => x.proposal),
    {
      enabled: !!id,
      select: transProposalDetail,
    },
  );
};

export const useVoteProposal = ({ hasVoted, source }: { hasVoted: boolean; source: string }) => {
  return useMutation(
    (id: string) => {
      return request
        .post<{ is_success: boolean }>(hasVoted ? '/proposal/unvote' : '/proposal/vote', {
          id,
          vote_source: !hasVoted ? source : undefined,
        })
        .then(res => {
          if (!res.is_success) {
            return Promise.reject();
          }
        });
    },
    {
      onError(err: any) {
        toastError(err || 'Failed to vote');
      },
    },
  );
};

export const useVoters = (id: string, start = 0, limit = 100) => {
  const url = '/proposal/voters';

  return useQuery(
    [url, id, start, limit],
    () =>
      request.get<{
        voters: ProposalVoter[];
        total_count: number;
      }>(url, {
        id,
        start,
        limit,
      }),
    {
      enabled: !!id,
    },
  );
};
